import qs from 'qs';
import {browserHistory} from 'react-router';

import logger from 'lib/logger';

/**
 * App-wide history facade. `browserHistory` is the react-router-shim object
 * backed by TanStack Router (see src/lib/router). It already exposes
 * push/replace/pushState/replaceState/go/goBack/goForward/getCurrentLocation,
 * so this module just re-exports it as `history` and layers on the query-param
 * helpers callers rely on.
 *
 * (The old isomorphic-SSR machinery — `resetServerHistory`,
 * `getServerRedirectRoute`, the in-memory history branch — was removed; SSR was
 * retired long ago and nothing references those exports.)
 */
export const history = browserHistory;

type QueryParams = Record<string, unknown>;

const modifyQueryParams = (replaceQuery = false) => {
  return (queryObj: QueryParams = {}) =>
    new Promise<void>((res) => {
      setTimeout(() => {
        const location = history.getCurrentLocation();
        let currentAsString = location.search;
        currentAsString = currentAsString ? currentAsString.slice(1) : '';
        const currentAsObj = qs.parse(currentAsString);
        const newQuery: QueryParams = replaceQuery ? queryObj : {...currentAsObj, ...queryObj};
        const filteredQuery = Object.keys(newQuery).reduce<QueryParams>((r, key) => {
          if (newQuery[key] !== null) {
            r[key] = newQuery[key];
          }
          return r;
        }, {});
        const queryString = qs.stringify(filteredQuery);
        history.push({
          pathname: location.pathname,
          search: queryString ? `?${queryString}` : '',
          hash: location.hash || ''
        });
        res();
      }, 0);
    });
};

/**
 * The difference between these two methods is whether or not the current query params will be kept. pushQueryParams
 * will merge the new query object with the current query params, while replaceQueryParams will, as its name suggests,
 * replace the current query params with the ones passed to it.
 *
 * To remove a query param, set its value to null - e.g. pushQueryParams({someVal: 123, someValToRemove null});
 */
export const pushQueryParams = modifyQueryParams();
export const replaceQueryParams = modifyQueryParams(true);

/**
 * Lets you selectively remove query params from current path by name. E.g. if you are at ?foo=123&bar=hello,
 * calling removeQueryParams('foo') will replace state to ?bar=hello
 */
export function removeQueryParams(...params: string[]) {
  setTimeout(() => {
    if (params.some((param) => typeof param !== 'string')) {
      logger.debug(
        'removeQueryParams function is bailing early as its required conditions were not met'
      );
      return;
    }

    const currentPath = history.getCurrentLocation().pathname;
    const currentQuery = history.getCurrentLocation().query || {};
    const filteredQuery = Object.keys(currentQuery).reduce<QueryParams>((r, property) => {
      if (!params.includes(property)) {
        r[property] = currentQuery[property];
      }
      return r;
    }, {});

    const currentHash = history.getCurrentLocation().hash || '';
    history.replace({
      pathname: currentPath,
      search: params.length > 0 ? '?' + qs.stringify(filteredQuery) : '',
      hash: currentHash
    });
  }, 0);
}

export function getQueryParamsAsObject() {
  return qs.parse(history.getCurrentLocation().search.replace(/^\?/, ''));
}

export function getQueryStringForParams(...params: string[]) {
  const currentParams = getQueryParamsAsObject();
  const matches = params
    .filter((param) => Object.prototype.hasOwnProperty.call(currentParams, param))
    .reduce<QueryParams>((acc, param) => {
      acc[param] = currentParams[param];
      return acc;
    }, {});
  return qs.stringify(matches);
}
